import React, { FC, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { doc, getDoc, updateDoc, collection, getDocs, query, where } from 'firebase/firestore';
import DealerInviteCard from '../../components/DealerInviteCard/DealerInviteCard';
import { UserAuth } from '../../AuthContext';
import { Box, Flex, Text, Heading, Spinner, Center, Avatar, Button } from '@chakra-ui/react';
import QRCode from 'qrcode';

const TIER_CONFIG: Record<string, { label: string; icon: string; color: string; bg: string }> = {
  home_certified: { label: 'Home Game Certified', icon: '🃏', color: '#22C55E', bg: '#0A1A10' },
  pc_certified:   { label: 'PC Certified Dealer', icon: '🎖', color: '#19E6CF', bg: '#0D1F1E' },
  pc_senior:      { label: 'PC Senior Dealer',    icon: '⭐', color: '#A855F7', bg: '#1A0D2E' },
  pc_master:      { label: 'PC Master Dealer',    icon: '👑', color: '#D4A017', bg: '#1A0F00' },
  pc_pro:         { label: 'PC Verified Pro',     icon: '💎', color: '#EF4444', bg: '#2D0D0D' },
};

const UPGRADE_PATHS: Record<string, { next: string; requirements: string[] }> = {
  home_certified: {
    next: 'PC Certified Dealer',
    requirements: [
      'Pass the Professional Certification test (30 questions, 85%)',
      'Complete the Professional track questionnaire',
    ],
  },
  pc_certified: {
    next: 'PC Senior Dealer',
    requirements: [
      '10+ rated events dealt through PokerCity',
      'Maintain a 4.0/5.0 average TD rating',
    ],
  },
  pc_senior: {
    next: 'PC Master Dealer',
    requirements: [
      'Pass the TDA Rules module',
      'Pass the Roberts Rules module',
      'Maintain a 4.5/5.0 average TD rating',
      'Earn 2 TD endorsements',
    ],
  },
  pc_master: {
    next: 'PC Verified Pro',
    requirements: [
      'Submit external credentials (dealing school, casino experience, or association membership)',
      'Human review by PokerCity staff',
    ],
  },
};

const AVAILABILITY_LABELS: Record<string, { label: string; color: string }> = {
  available:     { label: 'Available',         color: '#22C55E' },
  busy:          { label: 'Busy',              color: '#F59E0B' },
  not_available: { label: 'Not Taking Bookings', color: '#6B7590' },
};

const GAME_LABELS: Record<string, string> = {
  holdem:   "Texas Hold'em",
  omaha:    'Omaha',
  mixed:    'Mixed Games',
  stud:     'Stud',
  other:    'Other',
};

const DealerPassportPage: FC = () => {
  const { uid } = useParams<{ uid: string }>();
  const { user } = UserAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<any>(null);
  const [dealerData, setDealerData] = useState<any>(null);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [availUpdating, setAvailUpdating] = useState(false);
  const [travelZones, setTravelZones] = useState<any[]>([]);
  const [showAddZone, setShowAddZone] = useState(false);
  const [newZone, setNewZone] = useState({ city: '', radius_km: 50, from: '', until: '', note: '' });
  const [savingZone, setSavingZone] = useState(false);
  const [deletingZone, setDeletingZone] = useState<string|null>(null);
  const [pendingInvites, setPendingInvites] = useState<any[]>([]);
  const isOwner = user?.uid === uid;

  useEffect(() => {
    const load = async () => {
      if (!uid) return;
      try {
        const [userSnap, dealerSnap] = await Promise.all([
          getDoc(doc(db, 'users', uid)),
          getDoc(doc(db, 'dealers', uid)),
        ]);
        if (userSnap.exists()) setProfile(userSnap.data());
        if (dealerSnap.exists()) setDealerData(dealerSnap.data());

        if (uid) {
          try {
            const evSnap = await getDocs(
              query(collection(db,'events'),where('dealer_uid','==',uid),where('dealer_status','==','pending'))
            );
            const invites = await Promise.all(evSnap.docs.map(async d => {
              const ev = d.data();
              let tdName = 'Tournament Director';
              try {
                const tdSnap = await getDoc(doc(db,'users',ev.admin_uid));
                if(tdSnap.exists()) tdName = tdSnap.data().display_name ?? tdName;
              } catch {}
              return { eventId:d.id, eventName:ev.name??'Poker Event', eventStart:ev.start??'',
                tdUid:ev.admin_uid, tdName, deadline:ev.dealer_invite_deadline??'',
                urgent:ev.dealer_invite_urgent??false, dealerUid:uid };
            }));
            setPendingInvites(invites);
          } catch(e){ console.error(e); }
        }
        // Load travel zones
        if (uid) {
          try {
            const { collection: col, getDocs: gds } = await import('firebase/firestore');
            const zonesSnap = await gds(col(db, 'dealers', uid, 'travel_zones'));
            setTravelZones(zonesSnap.docs.map(d => ({ id: d.id, ...d.data() })));
          } catch {}
        }

        const url = `${window.location.origin}/dealer/${uid}`;
        const qr = await QRCode.toDataURL(url, { width: 200, margin: 1, color: { dark: '#000', light: '#fff' } });
        setQrDataUrl(qr);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [uid]);

  const addTravelZone = async () => {
    if (!uid || !isOwner || !newZone.city || savingZone) return;
    setSavingZone(true);
    try {
      // Geocode city
      let lat = null, lng = null, formatted = newZone.city;
      try {
        const geoResp = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(newZone.city)}&key=${process.env.REACT_APP_GOOGLE_API_KEY}`
        );
        const geoData = await geoResp.json();
        if (geoData.results?.[0]?.geometry?.location) {
          lat = geoData.results[0].geometry.location.lat;
          lng = geoData.results[0].geometry.location.lng;
          formatted = geoData.results[0].formatted_address;
        }
      } catch {}

      const { collection: col, addDoc: addD, serverTimestamp: sts } = await import('firebase/firestore');
      const zoneRef = await addD(col(db, 'dealers', uid, 'travel_zones'), {
        city: formatted,
        lat, lng,
        radius_km: newZone.radius_km,
        available_from: newZone.from,
        available_until: newZone.until,
        note: newZone.note,
        createdAt: sts(),
      });
      setTravelZones(z => [...z, { id: zoneRef.id, city: formatted, lat, lng,
        radius_km: newZone.radius_km, available_from: newZone.from,
        available_until: newZone.until, note: newZone.note }]);
      setNewZone({ city: '', radius_km: 50, from: '', until: '', note: '' });
      setShowAddZone(false);
    } catch (e) { console.error(e); }
    setSavingZone(false);
  };

  const deleteTravelZone = async (zoneId: string) => {
    if (!uid || !isOwner) return;
    setDeletingZone(zoneId);
    try {
      const { doc: d, deleteDoc: del } = await import('firebase/firestore');
      await del(d(db, 'dealers', uid, 'travel_zones', zoneId));
      setTravelZones(z => z.filter(zone => zone.id !== zoneId));
    } catch (e) { console.error(e); }
    setDeletingZone(null);
  };

  const setAvailability = async (val: string) => {
    if (!uid || !isOwner) return;
    setAvailUpdating(true);
    try {
      await updateDoc(doc(db, 'dealers', uid), { availability: val });
      setDealerData((d: any) => ({ ...d, availability: val }));
    } catch (e) { console.error(e); }
    setAvailUpdating(false);
  };

  if (loading) return <Center h="400px"><Spinner size="xl" color="teal.400" /></Center>;

  if (!dealerData || !profile) {
    return (
      <Center h="400px" flexDirection="column" gap={4}>
        <Text color="gray.400" fontSize="16px">Dealer Passport not found.</Text>
        {user && (
          <Button colorScheme="teal" onClick={() => navigate('/dealer-certification')}>
            Become a Dealer
          </Button>
        )}
      </Center>
    );
  }

  const tier = dealerData.tier ?? 'pc_certified';
  const tierCfg = TIER_CONFIG[tier] ?? TIER_CONFIG['pc_certified'];
  const avail = dealerData.availability ?? 'available';
  const availCfg = AVAILABILITY_LABELS[avail] ?? AVAILABILITY_LABELS['available'];
  const games: string[] = dealerData.games ?? ['holdem'];
  const isPending = dealerData.status === 'pending';
  const upgradePath = UPGRADE_PATHS[tier];

  const F = { ui: "'Rajdhani','Arial',sans-serif", mono: "'Share Tech Mono',monospace" };
  const C = { bg: '#080A0E', card: '#131720', border: '#1E2330', dim: '#6B7590', ink: '#C8D0E0', white: '#F0F4FF' };

  return (
    <Box maxW="680px" mx="auto" px={4} py={8} fontFamily={F.ui}>

      {/* Pending warning */}
      {isPending && isOwner && (
        <Box bg="#2D1F00" border="1px solid #D4A017" borderRadius={4} px={4} py={3} mb={4}>
          <Text fontSize="13px" color="#D4A017" fontWeight="600">
            ⏳ Your Dealer Passport is under review due to a conduct disclosure. You'll be notified once it's activated.
          </Text>
        </Box>
      )}

      {/* Passport Card */}
      <Box border={`2px solid ${tierCfg.color}`} borderRadius={4} overflow="hidden" mb={6}>

        {/* Header */}
        <Flex bg="#0A0A0A" px={5} py={3} justify="space-between" align="center">
          <Flex align="baseline" gap={0}>
            <Text color="#AAA" fontSize="16px" fontWeight="700" fontFamily={F.ui}>poker</Text>
            <Text color="#FFF" fontSize="16px" fontWeight="700" fontFamily={F.ui}>city</Text>
            <Text color="#444" fontSize="13px" ml="2px">♣</Text>
          </Flex>
          <Flex align="center" gap={3}>
            <Box px={3} py={1} bg={tierCfg.bg} border={`1px solid ${tierCfg.color}`} borderRadius={2}>
              <Text fontSize="10px" fontWeight="700" letterSpacing="0.18em" color={tierCfg.color} fontFamily={F.mono}>
                {tierCfg.icon} {tierCfg.label.toUpperCase()}
              </Text>
            </Box>
            <Box px={2} py={1} bg={avail === 'available' ? '#0A1A10' : '#1A1A1A'} borderRadius={2} border={`1px solid ${availCfg.color}44`}>
              <Flex align="center" gap={1}>
                <Box w="6px" h="6px" borderRadius="full" bg={availCfg.color} />
                <Text fontSize="10px" fontWeight="700" color={availCfg.color} letterSpacing="0.12em" fontFamily={F.mono}>
                  {availCfg.label.toUpperCase()}
                </Text>
              </Flex>
            </Box>
          </Flex>
        </Flex>

        {/* Body */}
        <Flex bg="white" p={5} gap={5} align="flex-start">

          {/* Left — avatar + QR */}
          <Flex direction="column" align="center" gap={3} flexShrink={0}>
            <Avatar size="xl" src={profile.avatar_url} name={profile.display_name} />
            {qrDataUrl && (
              <Box border="1px solid #E0E0E0" p={1} borderRadius={2}>
                <img src={qrDataUrl} alt="Dealer Passport QR" width={100} height={100} />
              </Box>
            )}
            <Text fontSize="9px" letterSpacing="0.12em" color="gray.400" textTransform="uppercase">Scan to verify</Text>
          </Flex>

          {/* Right — identity */}
          <Flex direction="column" gap={2} flex={1}>
            <Box>
              <Text fontSize="11px" letterSpacing="0.15em" color="gray.400" textTransform="uppercase" mb={1}>Dealer</Text>
              <Heading fontSize="26px" fontWeight="700" letterSpacing="-0.5px" color="gray.900" fontFamily={F.ui}>
                {profile.alias || profile.display_name}
              </Heading>
            </Box>

            <Flex gap={4} mt={1} flexWrap="wrap">
              <Box>
                <Text fontSize="10px" letterSpacing="0.12em" color="gray.400" textTransform="uppercase">Track</Text>
                <Text fontSize="13px" fontWeight="700" color="gray.700" fontFamily={F.ui}>
                  {dealerData.track === 'home' ? 'Home Game' : 'Professional'}
                </Text>
              </Box>
              <Box>
                <Text fontSize="10px" letterSpacing="0.12em" color="gray.400" textTransform="uppercase">Location</Text>
                <Text fontSize="13px" fontWeight="700" color="gray.700" fontFamily={F.ui}>
                  {dealerData.home_city || dealerData.questionnaire?.city || profile.city || 'Manitoba, CA'}
                </Text>
              </Box>
              {dealerData.track === 'professional' && (
                <Box>
                  <Text fontSize="10px" letterSpacing="0.12em" color="gray.400" textTransform="uppercase">Travel</Text>
                  <Text fontSize="13px" fontWeight="700" color="gray.700" fontFamily={F.ui}>
                    {dealerData.travel_radius_km ? `${dealerData.travel_radius_km === 999 ? 'Province-wide' : `${dealerData.travel_radius_km} km`}` : 'Local'}
                  </Text>
                </Box>
              )}
            </Flex>

            {/* Stats */}
            <Flex gap={5} mt={3} pt={3} borderTop="1px solid" borderColor="gray.100">
              <Flex direction="column" align="center" gap={1} minW="60px">
                <Text fontSize="20px" fontWeight="700" color="gray.800" fontFamily="mono">
                  {dealerData.event_count ?? 0}
                </Text>
                <Text fontSize="9px" letterSpacing="0.15em" textTransform="uppercase" color="gray.400">Events</Text>
              </Flex>
              {dealerData.track === 'professional' && (
                <>
                  <Flex direction="column" align="center" gap={1} minW="60px">
                    <Text fontSize="20px" fontWeight="700" color="gray.800" fontFamily="mono">
                      {dealerData.rating_count > 0 ? dealerData.avg_rating?.toFixed(1) : '—'}
                    </Text>
                    <Text fontSize="9px" letterSpacing="0.15em" textTransform="uppercase" color="gray.400">Avg Rating</Text>
                  </Flex>
                  <Flex direction="column" align="center" gap={1} minW="60px">
                    <Text fontSize="20px" fontWeight="700" color="gray.800" fontFamily="mono">
                      {dealerData.endorsements?.length ?? 0}
                    </Text>
                    <Text fontSize="9px" letterSpacing="0.15em" textTransform="uppercase" color="gray.400">Endorsements</Text>
                  </Flex>
                </>
              )}
            </Flex>

            {/* Games */}
            <Flex gap={2} flexWrap="wrap" mt={1}>
              {games.map(g => (
                <Box key={g} px={2} py={1} bg="gray.100" borderRadius={3}>
                  <Text fontSize="11px" fontWeight="600" color="gray.600">{GAME_LABELS[g] ?? g}</Text>
                </Box>
              ))}
            </Flex>
          </Flex>
        </Flex>

        {/* Footer strip */}
        <Flex bg="#F5F5F5" px={5} py={2} justify="space-between" align="center" borderTop="1px solid #E0E0E0">
          <Text fontSize="10px" letterSpacing="0.2em" color="gray.400" fontFamily="mono">
            POKERCITY DEALER PASSPORT · {tierCfg.label.toUpperCase()}
          </Text>
          <Text fontSize="10px" color="gray.400">pokercity.com</Text>
        </Flex>
      </Box>

      {/* Request Dealer — public CTA */}
      {!isOwner && (
        <Box border="1px solid #1E2330" bg="#0D1118" borderRadius={4} p={4} mb={6}>
          <Flex align="center" justify="space-between">
            <Box>
              <Text fontSize="13px" fontWeight="700" color="#F0F4FF" fontFamily={F.ui}>Request This Dealer</Text>
              <Text fontSize="11px" color="#6B7590" mt={1}>Available via the PCTDS marketplace — coming soon</Text>
            </Box>
            <Box px={4} py={2} bg="#1E2330" borderRadius={4} border="1px solid #333">
              <Text fontSize="11px" fontWeight="700" color="#6B7590" letterSpacing="0.1em" fontFamily={F.mono}>COMING SOON</Text>
            </Box>
          </Flex>
        </Box>
      )}

      {isOwner && pendingInvites.length > 0 && (
        <Box mb={6}>
          <Text style={{fontFamily:"'Share Tech Mono',monospace",fontSize:11,color:'#D4A017',
            letterSpacing:'0.2em',marginBottom:12,textTransform:'uppercase' as const}}>
            PENDING INVITES ({pendingInvites.length})
          </Text>
          {pendingInvites.map(inv=>(
            <DealerInviteCard key={inv.eventId} invite={inv}
              onResponded={(eventId)=>setPendingInvites(p=>p.filter(i=>i.eventId!==eventId))} />
          ))}
        </Box>
      )}
      {/* Availability controls — owner only */}
      {isOwner && (
        <Box border="1px solid #1E2330" bg="#0D1118" borderRadius={4} p={4} mb={6}>
          <Text fontSize="11px" fontWeight="700" letterSpacing="0.15em" color="#6B7590" textTransform="uppercase" mb={3} fontFamily={F.mono}>
            Your Availability
          </Text>
          <Flex gap={2} flexWrap="wrap">
            {Object.entries(AVAILABILITY_LABELS).map(([key, cfg]) => (
              <Box
                key={key}
                as="button"
                px={3} py={2}
                borderRadius={4}
                border="1px solid"
                borderColor={avail === key ? cfg.color : '#1E2330'}
                bg={avail === key ? `${cfg.color}18` : '#131720'}
                cursor={availUpdating ? 'wait' : 'pointer'}
                onClick={() => setAvailability(key)}
                opacity={availUpdating ? 0.7 : 1}
              >
                <Flex align="center" gap={2}>
                  <Box w="7px" h="7px" borderRadius="full" bg={cfg.color} />
                  <Text fontSize="12px" fontWeight="700" color={avail === key ? cfg.color : '#6B7590'} fontFamily={F.ui}>
                    {cfg.label}
                  </Text>
                </Flex>
              </Box>
            ))}
          </Flex>
        </Box>
      )}

      {/* Travel Zones */}
      <Box border="1px solid #1E2330" bg="#0D1118" borderRadius={4} p={4} mb={6}>
        <Flex justify="space-between" align="center" mb={3}>
          <Text style={{fontFamily:"'Share Tech Mono',monospace",fontSize:11,color:'#D4A017',
            letterSpacing:'0.2em',textTransform:'uppercase' as const}}>
            🗺 AVAILABILITY ZONES
          </Text>
          {isOwner && !showAddZone && (
            <Box as="button" px={3} py={1} bg="#131720" border="1px solid #1E2330"
              borderRadius={4} fontSize="11px" fontWeight="700" color="#19E6CF"
              fontFamily="'Rajdhani',sans-serif" cursor="pointer"
              onClick={() => setShowAddZone(true)}>
              + Add Zone
            </Box>
          )}
        </Flex>

        {/* Home zone */}
        <Flex align="center" gap={3} mb={3} p={3} bg="#131720" borderRadius={6}
          border="1px solid #1E2330">
          <Text style={{fontSize:16}}>🏠</Text>
          <Box flex={1}>
            <Text style={{fontFamily:"'Rajdhani',sans-serif",fontSize:14,fontWeight:700,color:'#F0F4FF'}}>
              {dealerData?.home_city || dealerData?.questionnaire?.city || 'Home Location'}
            </Text>
            <Text style={{fontFamily:"'Share Tech Mono',monospace",fontSize:11,color:'#6B7590'}}>
              Home · {dealerData?.travel_radius_km === 999 ? 'Province-wide' : `${dealerData?.travel_radius_km ?? 50} km radius`}
            </Text>
          </Box>
          <Text style={{fontFamily:"'Share Tech Mono',monospace",fontSize:10,color:'#22C55E',
            letterSpacing:'0.15em'}}>PERMANENT</Text>
        </Flex>

        {/* Travel zones */}
        {travelZones.map(zone => {
          const fromDate = zone.available_from ? new Date(zone.available_from).toLocaleDateString('en-CA',{month:'short',day:'numeric'}) : '';
          const untilDate = zone.available_until ? new Date(zone.available_until).toLocaleDateString('en-CA',{month:'short',day:'numeric'}) : '';
          const isActive = (!zone.available_from || new Date(zone.available_from) <= new Date()) &&
                          (!zone.available_until || new Date(zone.available_until) >= new Date());
          return (
            <Flex key={zone.id} align="center" gap={3} mb={2} p={3} bg="#131720"
              borderRadius={6} border={`1px solid ${isActive ? '#D4A017' : '#1E2330'}`}>
              <Text style={{fontSize:16}}>✈</Text>
              <Box flex={1}>
                <Text style={{fontFamily:"'Rajdhani',sans-serif",fontSize:14,fontWeight:700,color:'#F0F4FF'}}>
                  {zone.city}
                </Text>
                <Text style={{fontFamily:"'Share Tech Mono',monospace",fontSize:11,color:'#6B7590'}}>
                  {zone.radius_km === 999 ? 'Province-wide' : `${zone.radius_km} km`}
                  {fromDate && untilDate ? ` · ${fromDate} – ${untilDate}` : ''}
                  {zone.note ? ` · ${zone.note}` : ''}
                </Text>
              </Box>
              <Flex align="center" gap={2}>
                <Text style={{fontFamily:"'Share Tech Mono',monospace",fontSize:10,
                  color: isActive ? '#D4A017' : '#6B7590', letterSpacing:'0.15em'}}>
                  {isActive ? 'ACTIVE' : 'UPCOMING'}
                </Text>
                {isOwner && (
                  <Box as="button" px={2} py={1} bg="transparent" border="none"
                    color="#EF4444" fontSize="12px" cursor="pointer"
                    opacity={deletingZone === zone.id ? 0.5 : 1}
                    onClick={() => deleteTravelZone(zone.id)}>
                    ✕
                  </Box>
                )}
              </Flex>
            </Flex>
          );
        })}

        {travelZones.length === 0 && !showAddZone && (
          <Text style={{fontFamily:"'Rajdhani',sans-serif",fontSize:13,color:'#6B7590',
            fontStyle:'italic'}}>
            No travel zones added yet. Add a zone to show TDs where you'll be available.
          </Text>
        )}

        {/* Add zone form */}
        {isOwner && showAddZone && (
          <Box mt={2} p={3} bg="#131720" borderRadius={6} border="1px solid #D4A017">
            <Text style={{fontFamily:"'Share Tech Mono',monospace",fontSize:11,color:'#D4A017',
              letterSpacing:'0.15em',marginBottom:12}}>NEW TRAVEL ZONE</Text>
            <Flex direction="column" gap={2}>
              <input placeholder="City / Location (e.g. Vancouver, BC)"
                value={newZone.city}
                onChange={e => setNewZone(z => ({...z, city: e.target.value}))}
                style={{background:'#0D1118',border:'1px solid #1E2330',borderRadius:6,
                  padding:'8px 12px',color:'#F0F4FF',fontSize:13,
                  fontFamily:"'Rajdhani',sans-serif",width:'100%'}} />
              <Flex gap={2}>
                <select value={newZone.radius_km}
                  onChange={e => setNewZone(z => ({...z, radius_km: Number(e.target.value)}))}
                  style={{background:'#0D1118',border:'1px solid #1E2330',borderRadius:6,
                    padding:'8px 12px',color:'#F0F4FF',fontSize:13,flex:1,
                    fontFamily:"'Rajdhani',sans-serif"}}>
                  <option value={25}>25 km radius</option>
                  <option value={50}>50 km radius</option>
                  <option value={100}>100 km radius</option>
                  <option value={999}>Province-wide</option>
                </select>
              </Flex>
              <Flex gap={2}>
                <Box flex={1}>
                  <Text style={{fontSize:10,color:'#6B7590',marginBottom:4,
                    fontFamily:"'Share Tech Mono',monospace",letterSpacing:'0.1em'}}>FROM</Text>
                  <input type="date" value={newZone.from}
                    onChange={e => setNewZone(z => ({...z, from: e.target.value}))}
                    style={{background:'#0D1118',border:'1px solid #1E2330',borderRadius:6,
                      padding:'8px 12px',color:'#F0F4FF',fontSize:13,width:'100%',
                      fontFamily:"'Rajdhani',sans-serif"}} />
                </Box>
                <Box flex={1}>
                  <Text style={{fontSize:10,color:'#6B7590',marginBottom:4,
                    fontFamily:"'Share Tech Mono',monospace",letterSpacing:'0.1em'}}>UNTIL</Text>
                  <input type="date" value={newZone.until}
                    onChange={e => setNewZone(z => ({...z, until: e.target.value}))}
                    style={{background:'#0D1118',border:'1px solid #1E2330',borderRadius:6,
                      padding:'8px 12px',color:'#F0F4FF',fontSize:13,width:'100%',
                      fontFamily:"'Rajdhani',sans-serif"}} />
                </Box>
              </Flex>
              <input placeholder="Note (optional — e.g. visiting family)"
                value={newZone.note}
                onChange={e => setNewZone(z => ({...z, note: e.target.value}))}
                style={{background:'#0D1118',border:'1px solid #1E2330',borderRadius:6,
                  padding:'8px 12px',color:'#F0F4FF',fontSize:13,
                  fontFamily:"'Rajdhani',sans-serif",width:'100%'}} />
              <Flex gap={2} mt={1}>
                <Box as="button" px={4} py={2} bg="#052E16" border="1px solid #166534"
                  borderRadius={6} fontSize="13px" fontWeight="700" color="#86EFAC"
                  fontFamily="'Rajdhani',sans-serif" cursor={savingZone?'wait':'pointer'}
                  opacity={!newZone.city || savingZone ? 0.5 : 1}
                  onClick={addTravelZone}>
                  {savingZone ? 'Saving...' : 'Save Zone'}
                </Box>
                <Box as="button" px={4} py={2} bg="#131720" border="1px solid #1E2330"
                  borderRadius={6} fontSize="13px" fontWeight="700" color="#6B7590"
                  fontFamily="'Rajdhani',sans-serif" cursor="pointer"
                  onClick={() => { setShowAddZone(false); setNewZone({city:'',radius_km:50,from:'',until:'',note:''}); }}>
                  Cancel
                </Box>
              </Flex>
            </Flex>
          </Box>
        )}
      </Box>

      {/* Upgrade path — owner only */}
      {isOwner && upgradePath && (
        <Box border="1px solid #1E2330" bg="#0D1118" borderRadius={4} p={4} mb={6}>
          <Text fontSize="11px" fontWeight="700" letterSpacing="0.15em" color="#D4A017" textTransform="uppercase" mb={2} fontFamily={F.mono}>
            Path to {upgradePath.next}
          </Text>
          <Flex direction="column" gap={2} mt={2}>
            {upgradePath.requirements.map((req, i) => (
              <Flex key={i} align="flex-start" gap={3}>
                <Box w="16px" h="16px" borderRadius="full" border="1.5px solid #1E2330" mt="2px" flexShrink={0} />
                <Text fontSize="13px" color="#C8D0E0" lineHeight={1.6} fontFamily={F.ui}>{req}</Text>
              </Flex>
            ))}
          </Flex>
        </Box>
      )}

      {/* View Player Passport link */}
      <Flex justify="center">
        <Box
          as="button"
          onClick={() => navigate(`/passport/${uid}`)}
          fontSize="12px"
          color="#6B7590"
          fontFamily={F.mono}
          letterSpacing="0.1em"
          _hover={{ color: '#C8D0E0' }}
          transition="color 0.2s"
        >
          VIEW PLAYER PASSPORT →
        </Box>
      </Flex>

    </Box>
  );
};

export default DealerPassportPage;
